/* import __COLOCATED_TEMPLATE__ from './create-or-manage-connection.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import { task } from 'ember-concurrency-decorators';
import type ImportSource from 'embercom/models/import-service/import-source';
import { SourceType } from 'embercom/models/import-service/import-source';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type { TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';

interface Args {
  createMode?: boolean;
  isOpen: boolean;
  onClose: () => void;
  redirectLocation?: string;
  onImportSourceCreated?: (importSource: ImportSource, selectedDomain: string) => void;
}

interface Signature {
  Args: Args;
}

export default class SalesforceArticleSyncCreateOrManageConnection extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare openCenteredWindowService: $TSFixMe;

  @tracked selectedDomain = 'production';
  @tracked importSource: ImportSource | undefined;

  get isCreateMode() {
    return true;
  }

  @action startConnectFlow(_selectedDomain = 'production') {
    taskFor(this.startImportSourceFlow).perform();
  }

  @action onDomainChange(domain: string) {
    this.selectedDomain = domain;
  }

  @task({ drop: true })
  *startImportSourceFlow(): TaskGenerator<void> {
    if (!this.importSource) {
      this.importSource = yield this.knowledgeHubService.createImportSource({
        sourceType: SourceType.Salesforce,
      });
    }

    if (!this.importSource) {
      return;
    }
    let url = this.knowledgeHubService.getOAuthURL(
      this.importSource.id,
      this.selectedDomain === 'sandbox',
    );
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    let window = this.openCenteredWindowService.setup(url, 800, 1280, 'Import');
    if (window) {
      window.focus();
      yield this.waitForWindowClose(window);
    }

    yield this.importSource.reload();
    if (!this.importSource.hasCredentials) {
      yield this.importSource.remove({ notify: false });
    } else {
      this.args.onImportSourceCreated
        ? this.args.onImportSourceCreated(this.importSource, this.selectedDomain)
        : null;
    }
  }

  private waitForWindowClose(window: any): Promise<void> {
    return new Promise((resolve) => {
      let checkInterval = setInterval(() => {
        if (window.closed) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 200);
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SalesforceArticleSync::Steps::Connect::CreateOrManageConnection': typeof SalesforceArticleSyncCreateOrManageConnection;
    'knowledge-hub/salesforce-article-sync/steps/connect/create-or-manage-connection': typeof SalesforceArticleSyncCreateOrManageConnection;
  }
}
